import 'media-chrome';
import 'media-chrome/menu';
import 'media-chrome/media-theme-element';
import 'youtube-video-element';

// Launch controller for the custom Tailwind "Sutro" media players.
// Player markup lives in the _partials/partial_tw_*_player.html blueprints;
// this module clones the right blueprint on click and drives a single shared
// <dialog id="tw-media-modal"> so a page can host many past-performance links
// without instantiating a player per row.

const VOLUME_STORE = 'gf-media-volume';

const templateFor = new Map<string, string>([
    ['video', 'tw-video-player-tpl'],
    ['audio', 'tw-audio-player-tpl'],
    ['youtube', 'tw-youtube-player-tpl']
]);

function readVolume(): number {
    const stored = parseFloat(localStorage.getItem(VOLUME_STORE) ?? '');
    return Number.isFinite(stored) ? Math.min(1, Math.max(0, stored)) : 1;
}

function setup(): void {
    const dialog = document.getElementById('tw-media-modal') as HTMLDialogElement | null;
    const body = document.getElementById('tw-media-modal-body');
    if (!dialog || !body) return;

    const mediaSelector = 'video, audio, youtube-video';

    const currentMedia = (): HTMLMediaElement | null =>
        body.querySelector(mediaSelector) as HTMLMediaElement | null;

    const teardown = (): void => {
        const media = currentMedia();
        if (media) {
            try { media.pause(); } catch { /* ignore */ }
            media.removeAttribute('src');
        }
        body.replaceChildren();
    };

    const openPlayer = (type: string, src: string, title: string): void => {
        const templateId = templateFor.get(type);
        const template = templateId
            ? (document.getElementById(templateId) as HTMLTemplateElement | null)
            : null;
        if (!template || !src) return;

        teardown();

        const fragment = template.content.cloneNode(true) as DocumentFragment;
        const theme = fragment.querySelector('media-theme');
        const media = fragment.querySelector(mediaSelector) as HTMLMediaElement | null;

        if (media) {
            media.setAttribute('src', src);
            media.autoplay = true;
            media.removeAttribute('loop');
            media.volume = readVolume();
            media.addEventListener('volumechange', () => {
                if (!media.muted) localStorage.setItem(VOLUME_STORE, String(media.volume));
            });
        }

        if (theme && title) theme.setAttribute('mediatitle', title);

        body.appendChild(fragment);

        if (typeof dialog.showModal === 'function') {
            dialog.showModal();
        } else {
            dialog.setAttribute('open', '');
        }

        const active = currentMedia();
        if (active) {
            const played = active.play();
            if (played && typeof played.catch === 'function') {
                played.catch(() => { /* autoplay may be blocked; show play button */ });
            }
        }
    };

    document.addEventListener('click', (event) => {
        const target = event.target as Element | null;
        if (!target) return;

        const trigger = target.closest('[data-tw-player]') as HTMLElement | null;
        if (trigger) {
            event.preventDefault();
            openPlayer(
                trigger.dataset.twPlayer ?? '',
                trigger.dataset.twSrc ?? '',
                trigger.dataset.twTitle ?? ''
            );
            return;
        }

        if (target.closest('[data-tw-close]')) {
            dialog.close();
        }
    });

    // Close when clicking the dialog backdrop (the dialog element itself).
    dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('close', teardown);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
} else {
    setup();
}
